import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Send, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { TokenLogo } from "@/components/common/TokenLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminAssistant } from "@/components/admin/AdminAssistant";
import { ActivityLog } from "@/components/admin/ActivityLog";
import { PoolDepositCard } from "@/components/admin/PoolDepositCard";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const detailText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return fallback;
};
const num = (n, d = 4) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const TOKENS = ["FMI", "USDT", "FBTC"];
const INPUT = "bg-[#050505] border-[#2A2411] text-white";

const LoginForm = ({ onDone }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", form, { withCredentials: true });
      onDone();
    } catch (err) {
      setError(detailText(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="max-w-sm mx-auto border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader><CardTitle className="font-display flex items-center gap-2"><ShieldCheck size={18} className="text-[#D4AF37]" /> Admin login</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="admin-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT} />
          <Input data-testid="admin-password-input" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={INPUT} />
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="admin-login-btn" type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const WalletCard = ({ w, fmiPrice }) => (
  <Card data-testid={`admin-wallet-${w.key}`} className="border-[#2A2411] bg-[#0A0A0A]">
    <CardContent className="p-5 space-y-3">
      <div>
        <p className="font-display font-bold text-white">{w.label}</p>
        <p className="text-xs text-zinc-500">{w.description}</p>
        <Link to={`/address/${w.address}`} className="table-link font-mono text-[11px] break-all">{w.address}</Link>
      </div>
      <div className="space-y-1.5">
        {TOKENS.map((t) => (
          <div key={t} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-zinc-400"><TokenLogo symbol={t} size="xs" /> {t}</span>
            <span className="text-right">
              <span data-testid={`admin-balance-${w.key}-${t.toLowerCase()}`} className="block font-mono text-white break-all">{num(w.balances[t])}</span>
              {t === "FMI" && fmiPrice && w.balances.FMI != null && <span className="block text-zinc-500 text-[11px] font-mono">≈ ${num(w.balances.FMI * fmiPrice, 2)}</span>}
            </span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const SendForm = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ token: "USDT", to: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/send", { ...form, amount: Number(form.amount) }, { withCredentials: true });
      toast.success(`Sent ${form.amount} ${form.token} · ${data.txHash.slice(0, 12)}…`);
      setForm({ ...form, to: "", amount: "" });
      queryClient.invalidateQueries({ queryKey: ["adminOverview"] });
    } catch (err) {
      toast.error(detailText(err, "Send failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card data-testid="admin-send" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3"><CardTitle className="text-base font-display flex items-center gap-2"><Send size={16} className="text-[#D4AF37]" /> Send from treasury</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={form.token} onValueChange={(token) => setForm({ ...form, token })}>
            <SelectTrigger data-testid="admin-send-token" className={INPUT}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#2A2411]">
              {TOKENS.map((t) => (
                <SelectItem key={t} value={t} data-testid={`admin-send-token-${t.toLowerCase()}`} className="text-white focus:text-white">
                  <span className="flex items-center gap-2"><TokenLogo symbol={t} size="xs" /> {t}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input data-testid="admin-send-to" placeholder="Recipient 0x…" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value.trim() })} className={`${INPUT} font-mono text-xs sm:col-span-2`} />
          <Input data-testid="admin-send-amount" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={INPUT} />
          <Button data-testid="admin-send-btn" type="submit" disabled={busy || !form.to || !form.amount} className="sm:col-span-4 bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
            {busy ? <Loader2 size={16} className="animate-spin" /> : `Send ${form.token}`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const Dashboard = ({ me, onLogout, onPasswordChanged }) => {
  const { data, isError } = useQuery({ queryKey: ["adminOverview"], queryFn: () => api.get("/admin/overview", { withCredentials: true }).then((r) => r.data), refetchInterval: 10000 });
  const fee = data?.feeIncome;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl font-bold">Admin <span className="text-[#D4AF37]">Panel</span></h1>
        <div className="flex gap-2">
          <ChangePasswordDialog onChanged={onPasswordChanged} />
          <Button data-testid="admin-logout-btn" variant="outline" onClick={onLogout} className="border-[#2A2411] text-zinc-300"><LogOut size={14} className="mr-1.5" /> Sign out</Button>
        </div>
      </div>
      {!me.passwordChangedAt && (
        <p data-testid="admin-password-reminder" className="text-sm p-3 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#F3E5AB]">
          You are still using the initial password. Please change it now using "Change password".
        </p>
      )}
      {isError && <p data-testid="admin-error" className="text-red-400 text-sm">Could not load admin data</p>}
      {fee && (
        <div data-testid="admin-fee-income" className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Total fee income", `${num(fee.totalUsdt)} USDT`], ["FMI trade fees", `${num(fee.fmiTradesFees)} USDT`], ["FMI trade tax", `${num(fee.fmiTradesTax)} USDT`], ["FBTC desk fees", `${num(fee.fbtcFees)} USDT`]].map(([k, v]) => (
            <div key={k} className="p-4 rounded-lg bg-[#0A0A0A] border border-[#2A2411]"><p className="text-[11px] text-zinc-500 uppercase tracking-wider">{k}</p><p className="font-display text-lg font-bold text-white">{v}</p></div>
          ))}
        </div>
      )}
      <SendForm />
      <PoolDepositCard />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{data?.wallets.map((w) => <WalletCard key={w.key} w={w} fmiPrice={data.fmiPriceUsd} />)}</div>
      <AdminAssistant />
      <ActivityLog />
    </div>
  );
};

export default function AdminPage() {
  const queryClient = useQueryClient();
  const { data: me, isLoading, refetch } = useQuery({ queryKey: ["adminMe"], queryFn: () => api.get("/auth/me", { withCredentials: true }).then((r) => r.data).catch(() => null), retry: false });
  const logout = async () => {
    await api.post("/auth/logout", {}, { withCredentials: true });
    queryClient.removeQueries({ queryKey: ["adminOverview"] });
    refetch();
  };
  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      {isLoading && <div className="h-40 rounded-lg bg-[#0A0A0A] animate-pulse" />}
      {!isLoading && (me ? <Dashboard me={me} onLogout={logout} onPasswordChanged={refetch} /> : <LoginForm onDone={refetch} />)}
    </div>
  );
}
